"use client";

import {
  createContext,
  createElement,
  useContext,
  type ComponentType,
  type ReactNode,
} from "react";
import {
  MotionConfig,
  useReducedMotion,
  useReducedMotionConfig,
} from "motion/react";

const LookSystem = createContext(false);
const STILL = { duration: 0 };

/**
 * Tells the client components of a look-system section that they are on the new path. motion/react animates in script,
 * where look-classic-motion.css cannot reach: here it reduces motion for a visitor who prefers less and always at Ingen
 * (`motion` is the section's scope), and as motion reduces only transform and layout animations itself, the default
 * transition ends the rest at once. Renders no element.
 */
export function LookSystemProvider({
  children,
  motion,
}: {
  children: ReactNode;
  motion?: Record<string, string>;
}) {
  const still = motion?.["data-re-motion"] === "none";
  const reduced = useReducedMotion();
  return (
    <LookSystem.Provider value={true}>
      <MotionConfig
        reducedMotion={still ? "always" : "user"}
        {...(still || reduced ? { transition: STILL } : {})}
      >
        {children}
      </MotionConfig>
    </LookSystem.Provider>
  );
}

export function useLookSystem(): boolean {
  return useContext(LookSystem);
}

/**
 * `transition`, or one that ends at once where LookSystemProvider reduces motion. For a component that sets its own
 * transition, and for motion-number, whose own copy of motion no MotionConfig of ours reaches.
 */
export function useLookTransition<T extends object>(
  transition: T,
): T | typeof STILL {
  return useReducedMotionConfig() ? STILL : transition;
}

const MOTION_PROPS = new Set([
  "initial",
  "animate",
  "exit",
  "whileInView",
  "whileHover",
  "whileTap",
  "whileFocus",
  "whileDrag",
  "viewport",
  "variants",
  "transition",
  "custom",
  "inherit",
  "layout",
  "layoutId",
  "onAnimationStart",
  "onAnimationComplete",
  "onViewportEnter",
  "onViewportLeave",
  "onUpdate",
]);

/**
 * A legacy always-on entrance (a motion component from either library) that follows the level on the look system: there
 * it is a plain `tag` inside its section's reveal, which motion.css plays per the section's level and never with
 * reduced motion. Old websites render `Moving` as before.
 */
export function lookEntrance<P extends object>(
  Moving: ComponentType<P>,
  tag: "div" | "section",
) {
  return function LookEntrance(props: P) {
    if (!useLookSystem()) return <Moving {...props} />;
    return createElement(
      tag,
      Object.fromEntries(
        Object.entries(props).filter(([name]) => !MOTION_PROPS.has(name)),
      ),
    );
  };
}
